import { api, setCookie } from '@weareconceptstudio/core';

const flushPendingAddress = async () => {
	if (typeof window === 'undefined') return false;

	const raw = sessionStorage.getItem('pending_address');

	if (!raw) return false;

	sessionStorage.removeItem('pending_address');

	try {
		const res = await api.post('addresses', JSON.parse(raw));
		// Persist the new address ID so AddressProvider can select it after the page loads
		const created = res?.currentAddress ?? (Array.isArray(res?.addresses) ? res.addresses[res.addresses.length - 1] : null);
		if (created?.id) {
			sessionStorage.setItem('pending_address_id', String(created.id));
		}
	} catch {
		// ignore address sync errors; auth should still complete
	}

	return true;
};

export const murakamiUserHooks = {
	formatPrevRoute: (pathname, lang) => {
		if (!lang || lang === 'hy') {
			return pathname;
		}

		return `/${lang}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
	},
};

export const createMurakamiUserMethods = ({
	setState,
	getState,
	router,
	searchParams,
	getLangPath,
	userIntegration,
	api: apiClient,
}) => {
	const client = apiClient || api;
	const post = (resource, params, ...rest) => client.post(resource, params, ...rest);
	const cookieKey = userIntegration.cookieKey || 'accessToken';

	const resolvePostAuthRedirect = async () => {
		const hasPendingAddress = await flushPendingAddress();

		if (hasPendingAddress) {
			return getLangPath('menu');
		}

		return searchParams.get('r') || getState()?.prevRoute || getLangPath('account');
	};

	const applyAuth = async (response) => {
		const { accessToken, user } = response;

		setCookie(cookieKey, accessToken);
		const hadPendingAddress = await flushPendingAddress();

		setState((prev) => ({
			...prev,
			isLoggedIn: true,
			user,
		}));

		router.push(
			hadPendingAddress
				? getLangPath('menu')
				: searchParams.get('r') || getState()?.prevRoute || getLangPath('account')
		);

		return response;
	};

	return {
		toggleSubscribe: async (data) => {
			const res = await post('toggle-subscribe', data);

			setState((prev) => ({
				...prev,
				user: {
					...prev.user,
					...res,
				},
			}));

			return res;
		},
		signIn: async (data) => applyAuth(await post('login', data)),
		signUp: async (data) => applyAuth(await post('register', data)),

		// OTP-gated registration: validate the form + send the phone OTP without
		// creating the account yet. Stash the pending phone/data in context state
		// so VerifyCodeTemplate (unauthenticated "pending registration" mode) can
		// display the phone and re-send via `startRegistration` if needed.
		startRegistration: async (data) => {
			const res = await post('register/send-otp', data);

			setState((prev) => ({
				...prev,
				pendingRegistrationPhone: data.phone,
				pendingRegistrationData: data,
			}));

			return res;
		},

		// Verifies the OTP and creates the account, then logs the user in exactly
		// like `signUp` does (cookie + state + redirect via `applyAuth`).
		completeRegistration: async (code) => {
			const phone = getState()?.pendingRegistrationPhone;
			const res = await applyAuth(await post('register/verify-otp', { phone, code }));

			setState((prev) => ({
				...prev,
				pendingRegistrationPhone: undefined,
				pendingRegistrationData: undefined,
			}));

			return res;
		},

		updatePhone: async (phone) => {
			const currentUser = getState()?.user ?? {};
			const res = await post('update-account', { ...currentUser, phone });

			setState((prev) => ({
				...prev,
				user: {
					...prev.user,
					phone: res.phone ?? phone,
				},
			}));

			return res;
		},

		sendPhoneVerification: () => post('phone-verification/send'),

		verifyPhone: async (code) => {
			const res = await post('phone-verification/verify', { code });

			setState((prev) => ({
				...prev,
				user: {
					...prev.user,
					phone_verified_at: res.phone_verified_at,
				},
			}));

			router.push(await resolvePostAuthRedirect());

			return res;
		},
	};
};
